import { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users } from 'lucide-react';

const containerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '12px'
};

const defaultCenter = {
  lat: 19.0760,
  lng: 72.8777
};

export default function MapView({ grounds = [], liveLocations = [], onGroundClick }) {
  const [selectedGround, setSelectedGround] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [map, setMap] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBFw0Qbyq9zTvgKFZF60E-0-3W6YzG-c5I'
  });

  const onLoad = useCallback((map) => {
    setMap(map);
    
    // Fit bounds to show all markers
    if (grounds.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      grounds.forEach(ground => {
        bounds.extend({ lat: ground.latitude, lng: ground.longitude });
      });
      liveLocations.forEach(loc => {
        bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      });
      map.fitBounds(bounds);
    }
  }, [grounds, liveLocations]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!isLoaded) {
    return (
      <Card className="p-6 text-center">
        <div className="animate-pulse">Loading Map...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
          <MapPin className="h-5 w-5 mr-2 text-purple-600" />
          Live Location Map
        </h3>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 bg-purple-500 rounded-full"></div>
            <span className="text-gray-600">Grounds ({grounds.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-gray-600">Live Employees ({liveLocations.length})</span>
          </div>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={grounds.length > 0 ? { lat: grounds[0].latitude, lng: grounds[0].longitude } : defaultCenter}
        zoom={10}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
      >
        {/* Ground Markers with Radius Circles */}
        {grounds.map((ground) => (
          <div key={ground.id}>
            <Marker
              position={{ lat: ground.latitude, lng: ground.longitude }}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#a855f7" stroke="#6b21a8" stroke-width="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3" fill="white"></circle>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 40)
              }}
              onClick={() => setSelectedGround(ground)}
            />
            
            {/* Radius Circle */}
            <Circle
              center={{ lat: ground.latitude, lng: ground.longitude }}
              radius={ground.radius_km * 1000}
              options={{
                fillColor: '#a855f7',
                fillOpacity: 0.1,
                strokeColor: '#a855f7',
                strokeOpacity: 0.4,
                strokeWeight: 2
              }}
            />
            
            {selectedGround?.id === ground.id && (
              <InfoWindow
                position={{ lat: ground.latitude, lng: ground.longitude }}
                onCloseClick={() => setSelectedGround(null)}
              >
                <div className="p-2">
                  <h3 className="font-semibold text-gray-800">{ground.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{ground.address}</p>
                  <p className="text-xs text-gray-500 mt-2">Radius: {ground.radius_km} km</p>
                  <Badge className="mt-2 bg-purple-500">Ground Location</Badge>
                </div>
              </InfoWindow>
            )}
          </div>
        ))}

        {/* Employee Live Location Markers */}
        {liveLocations.map((location) => (
          <div key={location.id}>
            <Marker
              position={{ lat: location.latitude, lng: location.longitude }}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 24 24" fill="#10b981" stroke="#065f46" stroke-width="2">
                    <circle cx="12" cy="12" r="10" fill="#10b981"/>
                    <circle cx="12" cy="12" r="6" fill="white"/>
                    <circle cx="12" cy="12" r="3" fill="#10b981"/>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(35, 35),
                anchor: new window.google.maps.Point(17.5, 17.5)
              }}
              onClick={() => setSelectedEmployee(location)}
              animation={window.google.maps.Animation.BOUNCE}
            />
            
            {selectedEmployee?.id === location.id && (
              <InfoWindow
                position={{ lat: location.latitude, lng: location.longitude }}
                onCloseClick={() => setSelectedEmployee(null)}
              >
                <div className="p-2">
                  <h3 className="font-semibold text-gray-800">{location.user_name}</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Checked in: {new Date(location.check_in_time).toLocaleTimeString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Lat: {location.latitude.toFixed(4)}, Lng: {location.longitude.toFixed(4)}
                  </p>
                  <Badge className="mt-2 bg-green-500">Live</Badge>
                </div>
              </InfoWindow>
            )}
          </div>
        ))}
      </GoogleMap>

      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div className="bg-purple-50 p-3 rounded-lg">
          <p className="text-sm text-gray-600">Total Grounds</p>
          <p className="text-2xl font-bold text-purple-600">{grounds.length}</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-sm text-gray-600">Active Employees</p>
          <p className="text-2xl font-bold text-green-600">{liveLocations.length}</p>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-sm text-gray-600">Coverage Area</p>
          <p className="text-2xl font-bold text-blue-600">
            {grounds.length > 0 ? `${grounds[0].radius_km}km` : '-'}
          </p>
        </div>
      </div>
    </Card>
  );
}
